/** Site Operations > Equipment (Demo Mode only) - simplified, site-facing equipment view.
 * Site teams see only their own site's equipment, can check guidance and raise a service
 * request; they cannot edit manufacturer/catalogue records, maintenance rules, transfer or
 * decommission assets (that governance lives in the central Site Equipment screen). No
 * backend exists yet - all data is static mock data, matching the same "seeded illustrative
 * numbers" approach used by src/pages/EstateManagement. The "Raise Service Request" flow is a
 * local-only mock (no request is actually persisted or sent centrally).
 * lazy: submit only adds the request to local component state and shows a confirmation -
 * upgrade path is a real POST to the (not yet built) service-requests API once it exists. */

import { useState } from 'react';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel, MenuItem, Select, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, useTheme } from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useTranslation } from 'react-i18next';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { Pill } from '../../components/shared/Pill';
import CustomPagination from '../../components/forms/Pagination';

interface SiteAsset {
  name: string;
  zone: string;
  asset: string;
  status: string;
  lastService: string;
  nextService: string;
  openRequest: boolean;
}

const INITIAL_EQUIPMENT: SiteAsset[] = [
  { name: 'Rational iCombi Pro 10-1/1', zone: 'Main Kitchen', asset: 'TF-STMARY-KIT-0042', status: 'In Service', lastService: '2026-05-12', nextService: '2026-11-12', openRequest: false },
  { name: 'Walk-in Chiller', zone: 'Main Kitchen', asset: 'TF-STMARY-KIT-0018', status: 'In Service', lastService: '2026-03-02', nextService: '2026-09-02', openRequest: false },
  { name: 'Servery Counter Lighting', zone: 'Servery', asset: 'TF-STMARY-SRV-0007', status: 'Limited Use', lastService: '-', nextService: '-', openRequest: true },
];

const ISSUE_TYPES = ['Breakdown', 'Fault', 'Safety Concern', 'Maintenance Required', 'Cleaning Issue', 'Missing Equipment', 'Replacement Request', 'Installation Request', 'Inspection Required', 'General Estate Issue'];
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];

const statusColour = (status: string) => {
  if (status === 'Out of Service') return 'error';
  if (status === 'Limited Use') return 'warning';
  return 'success';
};

const ROWS_PER_PAGE = 10;

const SiteOperationsEquipment = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const [equipment, setEquipment] = useState<SiteAsset[]>(INITIAL_EQUIPMENT);
  const [dialogAsset, setDialogAsset] = useState<SiteAsset | null>(null);
  const [issueType, setIssueType] = useState('');
  const [priority, setPriority] = useState('');
  const [description, setDescription] = useState('');
  const [page, setPage] = useState(0);

  const openRequests = equipment.filter((e) => e.openRequest).length;
  const outOfService = equipment.filter((e) => e.status === 'Out of Service').length;
  const upcomingMaintenance = equipment.filter((e) => e.nextService !== '-').length;
  const pagedEquipment = equipment.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

  const handleClose = () => {
    setDialogAsset(null);
    setIssueType('');
    setPriority('');
    setDescription('');
  };

  const handleSubmit = () => {
    if (!dialogAsset) return;
    setEquipment((prev) => prev.map((e) => (e.asset === dialogAsset.asset ? { ...e, openRequest: true } : e)));
    handleClose();
  };

  return (
    <PageArea>
      <PageTitle>
        {t('siteOperationsEquipmentTitle', 'Equipment')}
      </PageTitle>

      <Grid container
        spacing={4}>
        <Grid item
          xs={12}
          sm={6}
          md={3}>
          <Section style={{ p: 4, mb: 0 }}>
            <Box display="flex"
              alignItems="center"
              justifyContent="space-between">
              <Box flex={1}>
                <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                  {t('siteOperationsMyEquipment', 'My Equipment')}
                </Box>
                <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                  {equipment.length}
                </Box>
              </Box>
              <Box sx={{ backgroundColor: '#1976d220', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1976d2' }}>
                <SvgIcon fontSize="medium"><BuildIcon /></SvgIcon>
              </Box>
            </Box>
          </Section>
        </Grid>
        <Grid item
          xs={12}
          sm={6}
          md={3}>
          <Section style={{ p: 4, mb: 0 }}>
            <Box display="flex"
              alignItems="center"
              justifyContent="space-between">
              <Box flex={1}>
                <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                  {t('serviceRequestsOpen', 'Open requests')}
                </Box>
                <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                  {openRequests}
                </Box>
              </Box>
              <Box sx={{ backgroundColor: '#ed6c0220', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ed6c02' }}>
                <SvgIcon fontSize="medium"><ReportProblemIcon /></SvgIcon>
              </Box>
            </Box>
          </Section>
        </Grid>
        <Grid item
          xs={12}
          sm={6}
          md={3}>
          <Section style={{ p: 4, mb: 0 }}>
            <Box display="flex"
              alignItems="center"
              justifyContent="space-between">
              <Box flex={1}>
                <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                  {t('siteOperationsOutOfService', 'Out of Service')}
                </Box>
                <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                  {outOfService}
                </Box>
              </Box>
              <Box sx={{ backgroundColor: '#d32f2f20', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d32f2f' }}>
                <SvgIcon fontSize="medium"><ErrorOutlineIcon /></SvgIcon>
              </Box>
            </Box>
          </Section>
        </Grid>
        <Grid item
          xs={12}
          sm={6}
          md={3}>
          <Section style={{ p: 4, mb: 0 }}>
            <Box display="flex"
              alignItems="center"
              justifyContent="space-between">
              <Box flex={1}>
                <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                  {t('siteOperationsUpcomingMaintenance', 'Upcoming Maintenance')}
                </Box>
                <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                  {upcomingMaintenance}
                </Box>
              </Box>
              <Box sx={{ backgroundColor: '#2e7d3220', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2e7d32' }}>
                <SvgIcon fontSize="medium"><EventAvailableIcon /></SvgIcon>
              </Box>
            </Box>
          </Section>
        </Grid>
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Section>
          <TableHeader>
            {t('siteOperationsEquipmentListTitle', 'Equipment at my site')}
            <Pill text={`${equipment.length} ${t('siteOperationsEquipmentUnit', 'Equipment')}`} />
          </TableHeader>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('equipmentCatalogueName', 'Equipment')}</TableCell>
                  <TableCell>{t('storeZonesName', 'Zone')}</TableCell>
                  <TableCell>{t('siteEquipmentAssetNumber', 'Asset Number')}</TableCell>
                  <TableCell>{t('estateManagementStatus', 'Status')}</TableCell>
                  <TableCell>{t('equipmentManagementLastService', 'Last Service')}</TableCell>
                  <TableCell>{t('siteEquipmentNextService', 'Next Service')}</TableCell>
                  <TableCell>{t('serviceRequestsOpen', 'Open Request')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedEquipment.map((row) => (
                  <TableRow key={row.asset}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.zone}</TableCell>
                    <TableCell>{row.asset}</TableCell>
                    <TableCell>
                      <Chip label={row.status}
                        size="small"
                        color={statusColour(row.status)}
                        variant="outlined" />
                    </TableCell>
                    <TableCell>{row.lastService}</TableCell>
                    <TableCell>{row.nextService}</TableCell>
                    <TableCell>{row.openRequest ? t('common.yes', 'Yes') : t('common.no', 'No')}</TableCell>
                    <TableCell>
                      <Button variant="secondary"
                        onClick={() => setDialogAsset(row)}>
                        {t('siteOperationsRaiseRequest', 'Raise Service Request')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination useNoDataMessage={false}
            page={page}
            setPage={setPage}
            maxPages={Math.ceil(equipment.length / ROWS_PER_PAGE)}
            breakpointForChangeDisplay={120000} />
        </Section>
      </Box>

      <Dialog open={!!dialogAsset}
        onClose={handleClose}
        fullWidth
        maxWidth="sm">
        <DialogTitle>{t('siteOperationsRaiseRequestTitle', 'Raise Service Request: {{asset}}', { asset: dialogAsset?.name ?? '' })}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('serviceRequestsType', 'Issue Type')}</InputLabel>
              <Select value={issueType}
                label={t('serviceRequestsType', 'Issue Type')}
                onChange={(e) => setIssueType(e.target.value)}>
                {ISSUE_TYPES.map((type) => (
                  <MenuItem key={type}
                    value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('serviceRequestsPriority', 'Priority')}</InputLabel>
              <Select value={priority}
                label={t('serviceRequestsPriority', 'Priority')}
                onChange={(e) => setPriority(e.target.value)}>
                {PRIORITIES.map((p) => (
                  <MenuItem key={p}
                    value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label={t('siteOperationsDescribeIssue', 'Describe the issue')}
              multiline
              minRows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth />
            <Button variant="secondary"
              component="label">
              {t('siteOperationsAttachPhoto', 'Attach Photo')}
              <input type="file"
                hidden
                accept="image/*"
                aria-label={t('siteOperationsAttachPhoto', 'Attach Photo')} />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="primary"
            disabled={!issueType || !priority}
            onClick={handleSubmit}>
            {t('common.submit', 'Submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default SiteOperationsEquipment;
